import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import {
  GOAL_ATTAINMENT_VARIANCE_FILTERS,
  GOAL_ATTAINMENT_VARIANCE_KPI_DEFINITIONS,
} from '@constants/Reports/goalAttainmentVariance.constants'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useGoalAttainmentFinance } from '@hooks/reports/useGoalAttainmentFinance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiCurrency,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'
import { toSentenceCase } from '@utils/textCase.util'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CHART_MARGIN_LEGEND = {
  top: 24,
  right: 12,
  left: 4,
  bottom: 0,
}
const REPORT_LEGEND_WRAPPER_STYLE = { paddingTop: 0, fontSize: 12 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_GROUPED_BAR_GAP = 4
const REPORT_GROUPED_BAR_SIZE = 32
const REPORT_YAXIS_WIDTH = 60
const REPORT_PERCENTAGE_Y_AXIS_TICKS = [0, 25, 50, 75, 100]

const GOAL_COLOR = 'var(--blue-400)'
const ACTUAL_COLOR = 'var(--orange-300)'

function formatDollarAbbrev(value: number): string {
  if (value === 0) return '$0'
  if (value >= 1000000) {
    const m = value / 1000000
    return `$${m.toFixed(2)}M`
  }
  const k = value / 1000
  return Number.isInteger(k) ? `$${k}K` : `$${k.toFixed(0)}K`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function GoalAttainmentVarianceReport(): React.ReactElement {
  const [productionView, setProductionView] =
    useState<ReportsDistributionView>('Count')
  const [collectionView, setCollectionView] =
    useState<ReportsDistributionView>('Count')
  const [runRateView, setRunRateView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } = useGoalAttainmentFinance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load goal attainment data. Please try again.',
  })

  const productionGoalKpi = useMemo(() => {
    const buckets = data?.productionGoalByPeriod.buckets ?? []
    return buckets.reduce((sum, b) => sum + (b.value ?? 0), 0)
  }, [data])

  const productionActual = data?.totalProduction.value ?? 0

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return GOAL_ATTAINMENT_VARIANCE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const variancePct =
      productionGoalKpi === 0
        ? 0
        : ((productionActual - productionGoalKpi) / productionGoalKpi) * 100

    const valueById: Record<string, number> = {
      'production-goal': productionGoalKpi,
      'production-actual': productionActual,
      collections: data.totalCollections.value,
      'production-variance': variancePct,
    }

    return GOAL_ATTAINMENT_VARIANCE_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'currency'
          ? formatKpiCurrency(valueById[def.id])
          : formatKpiPercent(valueById[def.id]),
    }))
  }, [data, loading, productionGoalKpi, productionActual])

  const productionChartData = useMemo(() => {
    if (!data) return []
    const goalByLabel = new Map(
      data.productionGoalByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.productionByPeriod.buckets.map((row) => ({
      month: row.label,
      goal: goalByLabel.get(row.label) ?? 0,
      actual: row.value,
    }))

    if (productionView === 'Count') {
      return rows.map((row) => ({
        month: row.month,
        goal: row.goal,
        actual: row.actual,
        goalLabel: formatDollarAbbrev(row.goal),
        actualLabel: formatDollarAbbrev(row.actual),
      }))
    }

    return rows.map((row) => {
      const pct =
        row.goal === 0 ? 0 : Math.round((row.actual / row.goal) * 1000) / 10
      return {
        month: row.month,
        goal: 100,
        actual: pct,
        goalLabel: '100%',
        actualLabel: `${pct}%`,
      }
    })
  }, [data, productionView])

  const productionYAxis = useMemo(() => {
    if (productionView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: REPORT_PERCENTAGE_Y_AXIS_TICKS,
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeDynamicDollarAxis(
      productionChartData.flatMap((row) => [row.goal, row.actual]),
    )
  }, [productionView, productionChartData])

  const collectionChartData = useMemo(() => {
    if (!data) return []
    const goalByLabel = new Map(
      data.collectionGoalByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.collectionByPeriod.buckets.map((row) => ({
      month: row.label,
      goal: goalByLabel.get(row.label) ?? 0,
      actual: row.value,
    }))

    if (collectionView === 'Count') {
      return rows.map((row) => ({
        month: row.month,
        goal: row.goal,
        actual: row.actual,
        goalLabel: formatDollarAbbrev(row.goal),
        actualLabel: formatDollarAbbrev(row.actual),
      }))
    }

    return rows.map((row) => {
      const pct =
        row.goal === 0 ? 0 : Math.round((row.actual / row.goal) * 1000) / 10
      return {
        month: row.month,
        goal: 100,
        actual: pct,
        goalLabel: '100%',
        actualLabel: `${pct}%`,
      }
    })
  }, [data, collectionView])

  const collectionYAxis = useMemo(() => {
    if (collectionView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: REPORT_PERCENTAGE_Y_AXIS_TICKS,
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeDynamicDollarAxis(
      collectionChartData.flatMap((row) => [row.goal, row.actual]),
    )
  }, [collectionView, collectionChartData])

  const runRateRows = useMemo(() => {
    if (!data) return []
    return [
      { label: 'Run Rate (MTD)', amount: data.dailyRunRateVsPace.runRate },
      { label: 'Required Pace', amount: data.dailyRunRateVsPace.requiredPace },
      { label: 'Gap (Required - Run)', amount: data.dailyRunRateVsPace.gap },
    ]
  }, [data])

  const runRateChartData = useMemo(() => {
    if (runRateView === 'Count') {
      return runRateRows.map((row) => ({
        label: row.label,
        barValue: row.amount,
        topLabel: formatDollarAbbrev(row.amount),
      }))
    }
    const pace =
      runRateRows.find((r) => r.label === 'Required Pace')?.amount || 1
    return runRateRows.map((row) => {
      const pct = Math.round((row.amount / pace) * 1000) / 10
      return {
        label: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [runRateRows, runRateView])

  const runRateYAxis = useMemo(() => {
    if (runRateView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: REPORT_PERCENTAGE_Y_AXIS_TICKS,
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeDynamicDollarAxis(runRateChartData.map((r) => r.barValue))
  }, [runRateView, runRateChartData])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalProduction.value,
      data?.totalCollections.value,
    ),
  )
  const productionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.productionByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const collectionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.collectionByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const runRateEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.dailyRunRateVsPace?.runRate,
      data?.dailyRunRateVsPace?.gap,
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-goal-attainment-variance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Goal Attainment & Variance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={GOAL_ATTAINMENT_VARIANCE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid reports-goal-attainment-variance__row reports-goal-attainment-variance__row--full">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  {toSentenceCase('Production Goal vs Actual')} ({bucketLabel})
                </ReportSectionHeading>
                {productionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={productionView}
                    onChange={(value) =>
                      setProductionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {productionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productionChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={REPORT_GROUPED_BAR_SIZE}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                        syncWithTicks
                        horizontalValues={
                          productionView === 'Percentage'
                            ? REPORT_PERCENTAGE_Y_AXIS_TICKS
                            : productionYAxis.ticks
                        }
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={productionYAxis.domain}
                        ticks={productionYAxis.ticks}
                        tickFormatter={productionYAxis.tickFormatter}
                        allowDataOverflow={productionView === 'Percentage'}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          productionView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="goal"
                        name={toSentenceCase('Production Goal')}
                        fill={GOAL_COLOR}
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        dataKey="actual"
                        name={toSentenceCase('Production Actual')}
                        fill={ACTUAL_COLOR}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid reports-goal-attainment-variance__row">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Collection Goal vs Actual ({bucketLabel})
                </ReportSectionHeading>
                {collectionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={collectionView}
                    onChange={(value) =>
                      setCollectionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {collectionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={collectionChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={REPORT_GROUPED_BAR_SIZE}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                        syncWithTicks
                        horizontalValues={
                          collectionView === 'Percentage'
                            ? REPORT_PERCENTAGE_Y_AXIS_TICKS
                            : collectionYAxis.ticks
                        }
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={collectionYAxis.domain}
                        ticks={collectionYAxis.ticks}
                        tickFormatter={collectionYAxis.tickFormatter}
                        allowDataOverflow={collectionView === 'Percentage'}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          collectionView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="goal"
                        name="Collection Goal"
                        fill={GOAL_COLOR}
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        dataKey="actual"
                        name="Collections Actual"
                        fill={ACTUAL_COLOR}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  {toSentenceCase('Daily Run Rate vs Required Pace')}
                </ReportSectionHeading>
                {runRateEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={runRateView}
                    onChange={(value) =>
                      setRunRateView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {runRateEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={runRateChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                        syncWithTicks
                        horizontalValues={
                          runRateView === 'Percentage'
                            ? REPORT_PERCENTAGE_Y_AXIS_TICKS
                            : runRateYAxis.ticks
                        }
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={runRateYAxis.domain}
                        ticks={runRateYAxis.ticks}
                        tickFormatter={runRateYAxis.tickFormatter}
                        allowDataOverflow={runRateView === 'Percentage'}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          runRateView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill={GOAL_COLOR}
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default GoalAttainmentVarianceReport
